// Dados mockados do protótipo — Dashboard Nexia (monitoramento de agentes · Luck Receptivo)
// "Agora" simulado para efeito de narrativa dos dados: 25/08/2026 11:17

export function pad(n) {
  return String(n).padStart(2, "0");
}

function fmt(date) {
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function addDays(date, delta) {
  const d = new Date(date);
  d.setDate(d.getDate() + delta);
  return d;
}

function parseBR(dateStr) {
  const [d, m, y] = dateStr.split("/").map(Number);
  return new Date(y, m - 1, d);
}

// "Hoje" agora é a data real do sistema — antes era fixa (25/08/2026), o que
// fazia sentido só enquanto o dashboard usava dado 100% mockado.
const _now = new Date();
export const TODAY = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());

export const nowLabel = TODAY.toLocaleDateString("pt-BR");

export const periodOptions = [
  { key: "hoje", label: "Hoje" },
  { key: "ontem", label: "Ontem" },
  { key: "7d", label: "Últimos 7 dias" },
  { key: "30d", label: "Últimos 30 dias" },
  { key: "custom", label: "Personalizado" },
];

export function getPeriodRange(key, customStart, customEnd) {
  switch (key) {
    case "hoje":
      return { start: TODAY, end: TODAY };
    case "ontem": {
      const y = addDays(TODAY, -1);
      return { start: y, end: y };
    }
    case "7d":
      return { start: addDays(TODAY, -6), end: TODAY };
    case "30d":
      return { start: addDays(TODAY, -29), end: TODAY };
    case "custom":
      return {
        start: customStart || addDays(TODAY, -6),
        end: customEnd || TODAY,
      };
    default:
      return { start: TODAY, end: TODAY };
  }
}

export function getPeriodLabel(key, range) {
  if (key === "hoje") return `Hoje · ${fmt(range.start)}`;
  if (key === "ontem") return `Ontem · ${fmt(range.start)}`;
  if (key === "7d") return `Últimos 7 dias · ${fmt(range.start)} – ${fmt(range.end)}`;
  if (key === "30d") return `Últimos 30 dias · ${fmt(range.start)} – ${fmt(range.end)}`;
  if (fmt(range.start) === fmt(range.end)) return fmt(range.start);
  return `${fmt(range.start)} – ${fmt(range.end)}`;
}

// Métricas-base por período (mockadas). "custom" é derivado por interpolação.
const baseMetrics = {
  hoje: {
    // Soma exata das execuções do Pipeline (58+80+74) e do Resolver (2+2+3) de hoje.
    processadas: 212,
    sucesso: 204,
    erro: 8,
    pendentes: 8,
    pendentesAgente: 6,
    pendentesManual: 2,
    resolvidas: 7,
    emailsEnviados: 16,
    emailsRecebidos: 13,
  },
  ontem: {
    // Soma exata das execuções do Pipeline (0+68) e do Resolver (4+2) de ontem.
    processadas: 68,
    sucesso: 65,
    erro: 3,
    pendentes: 7,
    pendentesAgente: 5,
    pendentesManual: 2,
    resolvidas: 6,
    emailsEnviados: 5,
    emailsRecebidos: 10,
  },
  "7d": {
    processadas: 823,
    sucesso: 758,
    erro: 65,
    pendentes: 41,
    pendentesAgente: 33,
    pendentesManual: 8,
    resolvidas: 63,
    emailsEnviados: 88,
    emailsRecebidos: 71,
  },
  "30d": {
    processadas: 3512,
    sucesso: 3244,
    erro: 268,
    pendentes: 158,
    pendentesAgente: 127,
    pendentesManual: 31,
    resolvidas: 241,
    emailsEnviados: 356,
    emailsRecebidos: 298,
  },
};

export function getPeriodMetrics(key, range) {
  if (baseMetrics[key]) return baseMetrics[key];

  // período personalizado: escala pela média diária dos últimos 30 dias
  const days = Math.max(
    1,
    Math.round((range.end.getTime() - range.start.getTime()) / 86400000) + 1
  );
  const base = baseMetrics["30d"];
  const scaled = {};
  Object.keys(base).forEach((k) => {
    scaled[k] = Math.max(0, Math.round((base[k] / 30) * days));
  });
  return scaled;
}

export function filterExecutionsByRange(executions, range) {
  return executions.filter((ex) => {
    const d = parseBR(ex.date);
    return d >= range.start && d <= range.end;
  });
}

export function lastExecutionLabel(filteredExecutions) {
  if (!filteredExecutions.length) return "Nenhuma execução no período";
  const last = filteredExecutions[0];
  return `${last.date} · ${last.start}`;
}

export const pipelineSteps = [
  { label: "Extração do Portal CVC", icon: "mdi-download-network-outline" },
  { label: "Importação no Phoenix", icon: "mdi-tray-arrow-down" },
  { label: "Processamento da fila", icon: "mdi-cog-sync-outline" },
  { label: "Identificação de pendências", icon: "mdi-clipboard-alert-outline" },
  { label: "Envio de e-mails", icon: "mdi-email-fast-outline" },
];

export const resolverSteps = [
  { label: "Verificação de respostas da CVC", icon: "mdi-email-search-outline" },
  { label: "Interpretação das correções", icon: "mdi-text-box-search-outline" },
  { label: "Busca da reserva", icon: "mdi-magnify" },
  { label: "Validação via API", icon: "mdi-api" },
  { label: "Alteração da reserva", icon: "mdi-pencil-outline" },
  { label: "Conclusão da pendência", icon: "mdi-check-circle-outline" },
];

const pipelineExecutions = [
  {
    id: "pl-1",
    date: "25/08/2026",
    start: "11:00",
    end: "11:24",
    duration: "24 min",
    status: "sucesso",
    qty: "58 processadas · 54 sucesso · 4 erro · 6 pendências",
    logs: [
      { time: "11:00", text: "Login no Portal da CVC" },
      { time: "11:02", text: "Filtro aplicado: 25/08/2026 a 25/08/2026" },
      { time: "11:03", text: "58 reservas encontradas" },
      { time: "11:05", text: "58 reservas extraídas" },
      { time: "11:07", text: "Login no Phoenix" },
      { time: "11:09", text: "Importação de 58 reservas iniciada" },
      { time: "11:14", text: "Importação finalizada" },
      { time: "11:14", text: "54 reservas importadas com sucesso" },
      { time: "11:14", text: "4 reservas apresentaram erro" },
      { time: "11:14", text: "Formato do arquivo 6a7f7148a399e4.22095745-2.99510.082501_303723297.xml incompatível" },
      { time: "11:14", text: "Formato do arquivo 8c1e42a0f7b21d.19442086-2.99511.082502_918273645.xml incompatível" },
      { time: "11:14", text: "Formato do arquivo 3f90b6d215aa77.30581274-2.99512.082503_552019384.xml incompatível" },
      { time: "11:14", text: "Formato do arquivo e21a5c8f603914.47726190-2.99513.082504_774105628.xml incompatível" },
      { time: "11:16", text: "Verificando pendências" },
      { time: "11:20", text: "6 reservas encontradas em pendência" },
      { time: "11:24", text: "6 e-mails enviados para a CVC" },
    ],
  },
  {
    id: "pl-2",
    date: "25/08/2026",
    start: "09:07",
    end: "09:28",
    duration: "21 min",
    status: "sucesso",
    qty: "80 processadas · 76 sucesso · 4 erro · 10 pendências",
    logs: [
      { time: "09:07", text: "Login no Portal da CVC" },
      { time: "09:09", text: "Filtro aplicado: 25/08/2026 a 25/08/2026" },
      { time: "09:10", text: "80 reservas encontradas" },
      { time: "09:12", text: "80 reservas extraídas" },
      { time: "09:14", text: "Login no Phoenix" },
      { time: "09:16", text: "Importação de 80 reservas iniciada" },
      { time: "09:22", text: "Importação finalizada" },
      { time: "09:22", text: "76 reservas importadas com sucesso" },
      { time: "09:22", text: "4 reservas apresentaram erro" },
      { time: "09:22", text: "Formato do arquivo 9b4d71ec2a8f06.15302947-2.99520.082501_601847293.xml incompatível" },
      { time: "09:22", text: "Formato do arquivo 4a70e19bc3d582.28841056-2.99521.082502_390276184.xml incompatível" },
      { time: "09:22", text: "Formato do arquivo c81f2e6a904b73.06617382-2.99522.082503_845190273.xml incompatível" },
      { time: "09:22", text: "Formato do arquivo 175d9a3ef62c04.39204871-2.99523.082504_120936748.xml incompatível" },
      { time: "09:24", text: "Verificando pendências" },
      { time: "09:25", text: "Filtro: Parceiro CVC + Execução 25/08/2026" },
      { time: "09:26", text: "10 reservas encontradas em pendência" },
      { time: "09:28", text: "10 e-mails enviados para a CVC" },
    ],
  },
  {
    id: "pl-3",
    date: "25/08/2026",
    start: "07:00",
    end: "07:19",
    duration: "19 min",
    status: "sucesso",
    qty: "74 processadas · 74 sucesso · 0 erro · 0 pendências",
    logs: [
      { time: "07:00", text: "Login no Portal da CVC" },
      { time: "07:02", text: "Filtro aplicado: 25/08/2026 a 25/08/2026" },
      { time: "07:03", text: "74 reservas encontradas" },
      { time: "07:05", text: "74 reservas extraídas" },
      { time: "07:07", text: "Login no Phoenix" },
      { time: "07:09", text: "Importação de 74 reservas iniciada" },
      { time: "07:16", text: "Importação finalizada" },
      { time: "07:16", text: "74 reservas importadas com sucesso" },
      { time: "07:16", text: "0 reservas apresentaram erro" },
      { time: "07:18", text: "Verificando pendências" },
      { time: "07:19", text: "Nenhuma reserva em pendência nesta execução" },
    ],
  },
  {
    id: "pl-4",
    date: "24/08/2026",
    start: "22:10",
    end: "22:33",
    duration: "23 min",
    status: "erro",
    qty: "0 processadas · execução abortada",
    logs: [
      { time: "22:10", text: "Login no Portal da CVC" },
      { time: "22:12", text: "Filtro aplicado: 24/08/2026 a 24/08/2026" },
      { time: "22:13", text: "68 reservas encontradas" },
      { time: "22:15", text: "68 reservas extraídas" },
      { time: "22:17", text: "Login no Phoenix" },
      { time: "22:19", text: "Falha de autenticação no Phoenix (timeout)" },
      { time: "22:25", text: "Nova tentativa de login no Phoenix" },
      { time: "22:31", text: "Falha de autenticação no Phoenix (timeout)" },
      {
        time: "22:33",
        text: "Execução abortada após 2 tentativas · erro registrado para retry automático",
      },
    ],
  },
  {
    id: "pl-5",
    date: "24/08/2026",
    start: "15:00",
    end: "15:26",
    duration: "26 min",
    status: "sucesso",
    qty: "68 processadas · 65 sucesso · 3 erro · 5 pendências",
    logs: [
      { time: "15:00", text: "Login no Portal da CVC" },
      { time: "15:02", text: "Filtro aplicado: 24/08/2026 a 24/08/2026" },
      { time: "15:03", text: "68 reservas encontradas" },
      { time: "15:05", text: "68 reservas extraídas" },
      { time: "15:07", text: "Login no Phoenix" },
      { time: "15:09", text: "Importação de 68 reservas iniciada" },
      { time: "15:20", text: "Importação finalizada" },
      { time: "15:20", text: "65 reservas importadas com sucesso" },
      { time: "15:20", text: "3 reservas apresentaram erro" },
      { time: "15:20", text: "Formato do arquivo a63f80c1e42975.51728306-2.99530.082401_267394815.xml incompatível" },
      { time: "15:20", text: "Formato do arquivo 0e9d4b7f218a56.84013927-2.99531.082402_593817264.xml incompatível" },
      { time: "15:20", text: "Formato do arquivo 5c2a761de8f309.72190548-2.99532.082403_408726193.xml incompatível" },
      { time: "15:23", text: "Verificando pendências" },
      { time: "15:24", text: "Filtro: Parceiro CVC + Execução 24/08/2026" },
      { time: "15:25", text: "5 reservas encontradas em pendência" },
      { time: "15:26", text: "5 e-mails enviados para a CVC" },
    ],
  },
];

const resolverExecutions = [
  {
    id: "rp-1",
    date: "25/08/2026",
    start: "10:30",
    end: "10:42",
    duration: "12 min",
    status: "sucesso",
    qty: "4 e-mails · 2 resolvidas · 2 aguardando resposta",
    logs: [
      { time: "10:30", text: "Verificando respostas da CVC" },
      { time: "10:31", text: "4 novos e-mails encontrados" },
      { time: "10:31", voucher: "2.99771.082501", text: "Correção solicitada pela CVC: alterar estabelecimento de destino para HOTEL RECIFE" },
      { time: "10:31", voucher: "2.99772.082502", text: "Correção solicitada pela CVC: alterar voo IN para LA - 3391" },
      { time: "10:31", voucher: "2.99773.082503", text: "E-mail identificado · aguardando confirmação da CVC" },
      { time: "10:31", voucher: "2.99774.082504", text: "E-mail identificado · aguardando confirmação da CVC" },
      { time: "10:32", voucher: "2.99771.082501", text: "Correção interpretada: Estabelecimento de Destino → HOTEL RECIFE" },
      { time: "10:33", voucher: "2.99771.082501", text: "Reserva localizada via API" },
      { time: "10:33", voucher: "2.99771.082501", text: "Valor validado com sucesso" },
      { time: "10:34", voucher: "2.99771.082501", text: "Reserva atualizada" },
      { time: "10:34", voucher: "2.99771.082501", text: "Pendência resolvida" },
      { time: "10:36", voucher: "2.99772.082502", text: "Correção interpretada: Voo IN → LA - 3391" },
      { time: "10:37", voucher: "2.99772.082502", text: "Reserva localizada via API" },
      { time: "10:38", voucher: "2.99772.082502", text: "Reserva atualizada" },
      { time: "10:38", voucher: "2.99772.082502", text: "Pendência resolvida" },
      { time: "10:40", voucher: "2.99773.082503", text: "Sem resposta da CVC até o fim da execução" },
      { time: "10:40", voucher: "2.99774.082504", text: "Sem resposta da CVC até o fim da execução" },
      {
        time: "10:42",
        text: "Execução finalizada · 4 e-mails · 2 resolvidas · 2 aguardando resposta",
      },
    ],
  },
  {
    id: "rp-2",
    date: "25/08/2026",
    start: "08:30",
    end: "08:41",
    duration: "11 min",
    status: "sucesso",
    qty: "6 e-mails · 2 resolvidas · 1 erro · 3 aguardando",
    logs: [
      { time: "08:30", text: "Verificando respostas da CVC" },
      { time: "08:31", text: "6 novos e-mails encontrados" },
      { time: "08:31", voucher: "2.99881.082501", text: "Correção solicitada pela CVC: alterar estabelecimento de destino para HOTEL PONTA NEGRA" },
      { time: "08:31", voucher: "2.99882.082502", text: "Correção solicitada pela CVC: alterar data do passeio para 27/08/2026" },
      { time: "08:31", voucher: "2.99883.082503", text: "Correção solicitada pela CVC: alterar estabelecimento de origem para POUSADA DO SOL" },
      { time: "08:31", voucher: "2.99884.082504", text: "E-mail identificado · aguardando confirmação da CVC" },
      { time: "08:31", voucher: "2.99885.082505", text: "E-mail identificado · aguardando confirmação da CVC" },
      { time: "08:31", voucher: "2.99886.082506", text: "E-mail identificado · aguardando confirmação da CVC" },
      { time: "08:32", voucher: "2.99881.082501", text: "Correção interpretada: Estabelecimento de Destino → HOTEL PONTA NEGRA" },
      { time: "08:33", voucher: "2.99881.082501", text: "Reserva localizada via API" },
      { time: "08:33", voucher: "2.99881.082501", text: "Valor validado com sucesso" },
      { time: "08:34", voucher: "2.99881.082501", text: "Reserva atualizada" },
      { time: "08:34", voucher: "2.99881.082501", text: "Pendência resolvida" },
      { time: "08:35", voucher: "2.99882.082502", text: "Correção interpretada: Data do Passeio → 27/08/2026" },
      { time: "08:36", voucher: "2.99882.082502", text: "Reserva localizada via API" },
      { time: "08:37", voucher: "2.99882.082502", text: "Valor validado com sucesso" },
      { time: "08:37", voucher: "2.99882.082502", text: "Reserva atualizada" },
      { time: "08:38", voucher: "2.99882.082502", text: "Pendência resolvida" },
      { time: "08:39", voucher: "2.99883.082503", text: "Correção interpretada: Estabelecimento de Origem → POUSADA DO SOL" },
      { time: "08:39", voucher: "2.99883.082503", text: "Reserva não localizada via API" },
      { time: "08:39", voucher: "2.99883.082503", text: "Pendência mantida em aberto · necessário retorno manual" },
      { time: "08:40", voucher: "2.99884.082504", text: "Sem resposta da CVC até o fim da execução" },
      { time: "08:40", voucher: "2.99885.082505", text: "Sem resposta da CVC até o fim da execução" },
      { time: "08:40", voucher: "2.99886.082506", text: "Sem resposta da CVC até o fim da execução" },
      {
        time: "08:41",
        text: "Execução finalizada · 6 e-mails · 2 resolvidas · 1 erro · 3 aguardando resposta",
      },
    ],
  },
  {
    id: "rp-3",
    date: "25/08/2026",
    start: "06:15",
    end: "06:24",
    duration: "9 min",
    status: "sucesso",
    qty: "3 e-mails · 3 resolvidas",
    logs: [
      { time: "06:15", text: "Verificando respostas da CVC" },
      { time: "06:16", text: "3 novos e-mails encontrados" },
      { time: "06:16", voucher: "2.99991.082501", text: "Correção solicitada pela CVC: alterar quantidade de pax para 2" },
      { time: "06:16", voucher: "2.99992.082502", text: "Correção solicitada pela CVC: alterar hotel de embarque para HOTEL PONTA NEGRA" },
      { time: "06:16", voucher: "2.99993.082503", text: "Correção solicitada pela CVC: alterar horário de embarque para 08:00" },
      { time: "06:17", voucher: "2.99991.082501", text: "Correção interpretada: Quantidade de PAX → 2" },
      { time: "06:18", voucher: "2.99991.082501", text: "Reserva localizada via API" },
      { time: "06:19", voucher: "2.99991.082501", text: "Reserva atualizada" },
      { time: "06:19", voucher: "2.99991.082501", text: "Pendência resolvida" },
      { time: "06:20", voucher: "2.99992.082502", text: "Correção interpretada: Hotel de Embarque → HOTEL PONTA NEGRA" },
      { time: "06:21", voucher: "2.99992.082502", text: "Reserva localizada via API" },
      { time: "06:21", voucher: "2.99992.082502", text: "Reserva atualizada" },
      { time: "06:22", voucher: "2.99992.082502", text: "Pendência resolvida" },
      { time: "06:22", voucher: "2.99993.082503", text: "Correção interpretada: Horário de Embarque → 08:00" },
      { time: "06:23", voucher: "2.99993.082503", text: "Reserva localizada via API" },
      { time: "06:23", voucher: "2.99993.082503", text: "Reserva atualizada" },
      { time: "06:24", voucher: "2.99993.082503", text: "Pendência resolvida" },
      {
        time: "06:24",
        text: "Execução finalizada · 3 e-mails · 3 resolvidas",
      },
    ],
  },
  {
    id: "rp-4",
    date: "24/08/2026",
    start: "20:00",
    end: "20:19",
    duration: "19 min",
    status: "sucesso",
    qty: "8 e-mails · 4 resolvidas · 1 erro · 3 aguardando",
    logs: [
      { time: "20:00", text: "Verificando respostas da CVC" },
      { time: "20:01", text: "8 novos e-mails encontrados" },
      { time: "20:01", voucher: "2.99701.082401", text: "Correção solicitada pela CVC: alterar quantidade de pax para 3" },
      { time: "20:01", voucher: "2.99702.082402", text: "Correção solicitada pela CVC: alterar hotel de embarque para HOTEL RECIFE" },
      { time: "20:01", voucher: "2.99703.082403", text: "Correção solicitada pela CVC: alterar estabelecimento de destino para POUSADA DO SOL" },
      { time: "20:01", voucher: "2.99704.082404", text: "Correção solicitada pela CVC: alterar horário de embarque para 07:30" },
      { time: "20:01", voucher: "2.99705.082405", text: "Correção solicitada pela CVC: alterar nome do passageiro para MARIA APARECIDA SOUZA" },
      { time: "20:01", voucher: "2.99706.082406", text: "E-mail identificado · aguardando confirmação da CVC" },
      { time: "20:01", voucher: "2.99707.082407", text: "E-mail identificado · aguardando confirmação da CVC" },
      { time: "20:01", voucher: "2.99708.082408", text: "E-mail identificado · aguardando confirmação da CVC" },
      { time: "20:02", voucher: "2.99701.082401", text: "Correção interpretada: Quantidade de PAX → 3" },
      { time: "20:03", voucher: "2.99701.082401", text: "Reserva localizada via API" },
      { time: "20:04", voucher: "2.99701.082401", text: "Reserva atualizada" },
      { time: "20:04", voucher: "2.99701.082401", text: "Pendência resolvida" },
      { time: "20:06", voucher: "2.99702.082402", text: "Correção interpretada: Hotel de Embarque → HOTEL RECIFE" },
      { time: "20:07", voucher: "2.99702.082402", text: "Reserva localizada via API" },
      { time: "20:08", voucher: "2.99702.082402", text: "Reserva atualizada" },
      { time: "20:08", voucher: "2.99702.082402", text: "Pendência resolvida" },
      { time: "20:09", voucher: "2.99703.082403", text: "Correção interpretada: Estabelecimento de Destino → POUSADA DO SOL" },
      { time: "20:10", voucher: "2.99703.082403", text: "Reserva não localizada via API" },
      { time: "20:10", voucher: "2.99703.082403", text: "Pendência marcada como erro · necessário retorno manual" },
      { time: "20:11", voucher: "2.99704.082404", text: "Correção interpretada: Horário de Embarque → 07:30" },
      { time: "20:12", voucher: "2.99704.082404", text: "Reserva localizada via API" },
      { time: "20:13", voucher: "2.99704.082404", text: "Reserva atualizada" },
      { time: "20:13", voucher: "2.99704.082404", text: "Pendência resolvida" },
      { time: "20:14", voucher: "2.99705.082405", text: "Correção interpretada: Nome do Passageiro → MARIA APARECIDA SOUZA" },
      { time: "20:14", voucher: "2.99705.082405", text: "Reserva localizada via API" },
      { time: "20:15", voucher: "2.99705.082405", text: "Reserva atualizada" },
      { time: "20:15", voucher: "2.99705.082405", text: "Pendência resolvida" },
      { time: "20:17", voucher: "2.99706.082406", text: "Sem resposta da CVC até o fim da execução" },
      { time: "20:17", voucher: "2.99707.082407", text: "Sem resposta da CVC até o fim da execução" },
      { time: "20:17", voucher: "2.99708.082408", text: "Sem resposta da CVC até o fim da execução" },
      {
        time: "20:19",
        text: "Execução finalizada · 8 e-mails · 4 resolvidas · 1 erro · 3 aguardando resposta",
      },
    ],
  },
  {
    id: "rp-5",
    date: "24/08/2026",
    start: "14:00",
    end: "14:10",
    duration: "10 min",
    status: "sucesso",
    qty: "2 e-mails · 2 resolvidas",
    logs: [
      { time: "14:00", text: "Verificando respostas da CVC" },
      { time: "14:01", text: "2 novos e-mails encontrados" },
      { time: "14:01", voucher: "2.99601.082401", text: "Correção solicitada pela CVC: alterar nome do passageiro para CARLOS EDUARDO SILVA" },
      { time: "14:01", voucher: "2.99602.082402", text: "Correção solicitada pela CVC: alterar local de embarque para HOTEL PONTA NEGRA" },
      { time: "14:02", voucher: "2.99601.082401", text: "Correção interpretada: Nome do Passageiro → CARLOS EDUARDO SILVA" },
      { time: "14:03", voucher: "2.99601.082401", text: "Reserva localizada via API" },
      { time: "14:04", voucher: "2.99601.082401", text: "Reserva atualizada" },
      { time: "14:04", voucher: "2.99601.082401", text: "Pendência resolvida" },
      { time: "14:06", voucher: "2.99602.082402", text: "Correção interpretada: Local de Embarque → HOTEL PONTA NEGRA" },
      { time: "14:07", voucher: "2.99602.082402", text: "Reserva localizada via API" },
      { time: "14:08", voucher: "2.99602.082402", text: "Reserva atualizada" },
      { time: "14:08", voucher: "2.99602.082402", text: "Pendência resolvida" },
      {
        time: "14:10",
        text: "Execução finalizada · 2 e-mails · 2 resolvidas",
      },
    ],
  },
];

export const agents = [
  {
    key: "cvc",
    name: "CVC",
    icon: "mdi-airplane-takeoff",
    expandable: true,
    subAgents: [
      {
        key: "pipeline-cvc",
        name: "Pipeline CVC",
        description: "Extração, importação e envio de emails para CVC",
        icon: "mdi-source-branch",
        frequency: "A cada 2 horas · 12x por dia",
        nextRun: "Hoje · 13:00",
        currentStepIndex: null,
        steps: pipelineSteps,
        executions: pipelineExecutions,
      },
      {
        key: "resolver-cvc",
        name: "Resolver Pendências CVC",
        description: "Leitura das respostas da CVC e correção das reservas pendentes",
        icon: "mdi-email-check-outline",
        frequency: "A cada 2 horas · 12x por dia",
        nextRun: "Hoje · 12:30",
        currentStepIndex: null,
        steps: resolverSteps,
        executions: resolverExecutions,
      },
    ],
  },
  {
    key: "azul",
    name: "Azul",
    icon: "mdi-airplane",
    expandable: false,
    subAgents: [],
  },
  {
    key: "decolar",
    name: "Decolar",
    icon: "mdi-airplane-marker",
    expandable: false,
    subAgents: [],
  },
];

// Roteiro de log para o acionamento manual ("Executar agora") de cada subagente.
// Os vouchers são gerados com a data real do momento em que o botão é clicado.
function randomVoucher(seq) {
  const now = new Date();
  const id = 90000 + Math.floor(Math.random() * 9000);
  return `2.${id}.${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(seq)}`;
}

function randomHex(len) {
  let s = "";
  for (let i = 0; i < len; i++) s += Math.floor(Math.random() * 16).toString(16);
  return s;
}

function randomDigits(len) {
  let s = "";
  for (let i = 0; i < len; i++) s += Math.floor(Math.random() * 10);
  return s;
}

// Erro real e mais comum do Pipeline CVC: o Portal entrega o arquivo XML da reserva
// num formato que o parser de importação do Phoenix não reconhece.
function xmlFormatError(voucher) {
  const fileName = `${randomHex(14)}.${randomDigits(8)}-${voucher}_${randomDigits(9)}.xml`;
  return `Formato do arquivo ${fileName} incompatível`;
}

// Cada linha do roteiro é { text, voucher? } — quando "voucher" está presente,
// o LogTimeline agrupa a linha dentro do card daquele voucher.
export function buildLiveRunScript(subAgentKey) {
  if (subAgentKey === "pipeline-cvc") {
    const e1 = randomVoucher(1);
    const e2 = randomVoucher(2);
    const e3 = randomVoucher(3);
    const e4 = randomVoucher(4);
    return [
      { text: "Login no Portal da CVC" },
      { text: "Filtro aplicado: hoje" },
      { text: "63 reservas encontradas" },
      { text: "63 reservas extraídas" },
      { text: "Login no Phoenix" },
      { text: "Importação de 63 reservas iniciada" },
      { text: "Importação finalizada" },
      { text: "59 reservas importadas com sucesso" },
      { text: "4 reservas apresentaram erro" },
      { text: xmlFormatError(e1) },
      { text: xmlFormatError(e2) },
      { text: xmlFormatError(e3) },
      { text: xmlFormatError(e4) },
      { text: "Verificando pendências" },
      { text: "7 reservas encontradas em pendência" },
      { text: "7 e-mails enviados para a CVC" },
      { text: "Execução finalizada · 63 processadas · 59 sucesso · 4 erro · 7 pendências" },
    ];
  }

  const v1 = randomVoucher(1);
  const v2 = randomVoucher(2);
  const v3 = randomVoucher(3);
  return [
    { text: "Verificando respostas da CVC" },
    { text: "3 novos e-mails encontrados" },
    { voucher: v1, text: "Correção solicitada pela CVC: alterar estabelecimento de destino para HOTEL RECIFE" },
    { voucher: v2, text: "Correção solicitada pela CVC: alterar voo IN para LA - 3391" },
    { voucher: v3, text: "E-mail identificado · aguardando confirmação da CVC" },
    { voucher: v1, text: "Correção interpretada: Estabelecimento de Destino → HOTEL RECIFE" },
    { voucher: v1, text: "Reserva localizada via API" },
    { voucher: v1, text: "Reserva atualizada" },
    { voucher: v1, text: "Pendência resolvida" },
    { voucher: v2, text: "Correção interpretada: Voo IN → LA - 3391" },
    { voucher: v2, text: "Reserva localizada via API" },
    { voucher: v2, text: "Reserva atualizada" },
    { voucher: v2, text: "Pendência resolvida" },
    { voucher: v3, text: "Sem resposta da CVC até o fim da execução" },
    { text: "Execução finalizada · 3 e-mails · 2 resolvidas · 1 aguardando resposta" },
  ];
}
